import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Card, EmptyState, ErrorState, IconInfoCircle, Page, PageHeader, Skeleton } from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import { ReasonSelectModal } from '../components/ReasonSelectModal';
import { REFUND_REASON_ETC_CODE, REFUND_REASON_TEXT_MAX_LENGTH } from '../constants/refundReasons';

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** BE 에러 응답의 실제 사유(BusinessException의 data 필드, C-03 §4.3 관례)를 뽑아낸다 — 사용 여부
 *  판정 실패("서비스 이용 내역이 있어 환불이 어려워요") 등 사유별 문구를 그대로 보여주기 위함
 *  (PaymentMethodPage.tsx extractErrorReason과 동일 패턴). 없으면 message, 그마저 없으면 fallback. */
function extractErrorReason(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { data?: string; message?: string } } })?.response?.data;
  return data?.data ?? data?.message ?? fallback;
}

/** PAYMENT_STATUS_UNCONFIRMED(2026-09-11 BE 신설) — ExLink 자체가 응답 불가해 서버도 토스가 실제로
 *  취소를 처리했는지 모르는 상태(CheckoutPage.tsx와 동일 코드). "환불 실패"가 아니라 "아직 모름"이라
 *  곧바로 다시 누르면 이미 처리된 취소를 또 시도하는 혼란으로 이어질 수 있어, 문구를 구분해서
 *  재시도를 말린다. */
function isUnconfirmedPaymentStatus(error: unknown): boolean {
  const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
  return code === 'PAYMENT_STATUS_UNCONFIRMED';
}

/**
 * C-10(환불 신청 화면). 진입경로: 구독·결제 관리(C-2) 해지 패널의 "환불신청" 버튼 하나뿐이다 —
 * 뒤로가기(←)도 결제내역(C-4)이 아니라 그 해지 패널로 돌아간다. 스튜디오당 활성 구독은 하나뿐이라
 * "지금 해지하려는 이 구독의 결제 건" 하나만 다루면 되므로, 여러 건 중 고르는 선택 UI는 두지 않고
 * 서버가 내려주는 가장 최근 미환불 건 1건만 그대로 보여준다(사용자 결정, 2026-09-08 — "구독 해지 →
 * 환불신청 버튼을 누르는 순간"이 곧 신청 대상을 특정하는 시점이라는 데 착안).
 * <p>
 * 이 화면 자체는 신청 가능한 건을 "보여주기"만 하고, 사유 입력·제출은 "환불하기"/"환불 신청하기"
 * 버튼을 눌러야 뜨는 팝업({@link ReasonSelectModal} — SubscriptionManagePage의 해지 사유 팝업과
 * 공용 컴포넌트, 2026-09-17 통합)에서 처리한다(사용자 결정, 2026-09-08).
 * <p>
 * 결제 후 7일 이내는 사용여부와 무관하게 무조건 전액 즉시 환불(사용자 결정, 2026-09-02 — 목업에
 * 있던 "사용함/안함" 선택 UI·일할계산 박스는 만들지 않았다).
 * <p>
 * 7일이 지난 건은 이 자기서비스 화면에서 신청 버튼 자체를 노출하지 않는다 — BE(SubscriptionService.
 * processRefund)도 7일이 지나면 사유와 무관하게 즉시 거절한다(REFUND_NOT_ELIGIBLE, 2026-09-17 —
 * "관리자 승인 큐로 돌린다"는 이전 정책은 관리자 화면·권한체계 자체가 없어 제거함, 사용자 결정).
 * 그래서 `payment.refundEligible=false`인 건은 카드에 "신청 불가(7일 경과)" 배지와 고객센터 안내
 * 문구만 보여준다 — alreadyUsed(이미 이용함) 케이스와 동일한 "정보만 보여주고 버튼 없음" 패턴.
 * 7일 경과 건을 고객센터가 어떻게 처리할지(수동 환불 등)는 아직 별도 기획이 필요한 영역.
 */
export function RefundRequestPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  // useCallback으로 참조 고정 — inline 함수면 이 페이지가 리렌더될 때마다(예: eligiblePaymentsQuery의
  // 백그라운드 refetch로 인한 리렌더) 새 함수가 만들어져 ReasonSelectModal → Modal의 포커스 트랩
  // effect(onClose 의존)가 재실행되고, 입력 도중 포커스가 닫기[x]로 튀는 버그가 있었다(2026-09-17
  // 발견 — ReasonSelectModal 자체의 handleClose를 useCallback으로 감싼 것만으로는 부족했다,
  // 이 프롭 자체가 매번 새로 생성되면 그 메모이제이션까지 무효화되기 때문).
  const handleModalClose = useCallback(() => setModalOpen(false), []);

  const eligiblePaymentsQuery = useQuery({
    queryKey: ['billing', 'refundEligiblePayments'],
    queryFn: async ({ signal }) => (await billingApi.getRefundEligiblePayments(signal)).data.data,
  });

  // BE가 이미 "신청 가능(7일 이내)한 건만, 최신순"으로 내려준다 — 스튜디오당 활성 구독은 하나뿐이라
  // 가장 최근 1건이 곧 지금 해지하려는 구독의 결제 건이다.
  const payment = eligiblePaymentsQuery.data?.[0] ?? null;

  const refundMutation = useMutation({
    // 팝업(ReasonSelectModal)은 payment가 있을 때만 열 수 있으므로(아래 JSX) 이 시점엔 항상 존재한다.
    mutationFn: (vars: { reasonCode: string; reasonText?: string }) =>
      billingApi.requestRefund(
        { paymentId: payment!.paymentId, reasonCode: vars.reasonCode, reasonText: vars.reasonText },
        crypto.randomUUID(),
      ),
    onSuccess: () => {
      setModalOpen(false);
      // 성공 응답은 이제 항상 즉시 처리 완료다(운영자 승인 대기 경로는 2026-09-17 제거, RefundResp.status
      // 참고) — 분기 없이 고정 문구.
      toast.success('환불 신청이 완료됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'refundEligiblePayments'] });
      navigate('/agents/billing/invoices');
    },
    onError: (error: unknown) => {
      if (isUnconfirmedPaymentStatus(error)) {
        // 확정 실패가 아니다 — 지금 다시 누르면 이미 처리됐을 수도 있는 취소를 또 시도하게 된다.
        // 잠시 기다렸다가 결제내역에서 실제로 반영됐는지 새로고침해 확인하도록 안내한다.
        toast.error('환불 처리 결과를 확인하는 중이에요. 곧바로 다시 시도하지 마시고, 잠시 후 결제내역에서 반영 여부를 확인해주세요.');
        return;
      }
      toast.error(extractErrorReason(error, '환불 처리에 실패했습니다. 잠시 후 다시 시도해주세요.'));
    },
  });

  return (
    <Page width="md">
      <PageHeader
        title="환불 신청"
        subtitle="결제 후 7일 이내 건만 환불 신청이 가능해요"
        onBack={() => navigate('/agents/billing/manage')}
      />

      <Card className="mt-06">
        {eligiblePaymentsQuery.isLoading ? (
          <Skeleton height={72} />
        ) : eligiblePaymentsQuery.isError ? (
          <ErrorState title="결제 내역을 불러오지 못했습니다" />
        ) : payment ? (
          <>
            <h3 className="text-title-sm text-text">환불 신청할 결제 건</h3>
            <div className="mt-04 flex items-center justify-between gap-md">
              <div>
                <p className="text-body-sm font-semibold text-text">{payment.description}</p>
                <p className="mt-01 text-caption text-text-muted">
                  {formatDate(payment.paidDtm)} 결제
                  {payment.refundEligible && ` · D+${7 - payment.remainingDays}`}
                </p>
              </div>
              <div className="flex flex-col items-end gap-xs">
                <span className="text-body-sm font-semibold text-text">₩{formatWon(payment.paymentAmt)}</span>
                {/* 7일 경과 건은 BE(processRefund)도 즉시 거절한다(REFUND_NOT_ELIGIBLE) — 이
                    자기서비스 화면은 신청 버튼 자체를 노출하지 않고 고객센터 문의로 안내한다
                    (2026-09-17, 사용자 결정 — 관리자 승인 큐 정책 제거). */}
                {!payment.refundEligible ? (
                  <Badge status="mute">
                    신청 불가 (7일 경과)
                  </Badge>
                ) : payment.alreadyUsed ? (
                  <Badge status="mute">
                    이미 이용함
                  </Badge>
                ) : (
                  <Badge status="success">
                    신청 가능 ({payment.remainingDays}일 남음)
                  </Badge>
                )}
              </div>
            </div>

            {payment.refundEligible && !payment.alreadyUsed ? (
              <Button variant="primary" className="mt-04 w-full" onClick={() => setModalOpen(true)}>
                환불하기
              </Button>
            ) : (
              <p className="mt-03 text-caption text-text-muted">
                {!payment.refundEligible
                  ? '결제 후 7일이 지나 이 화면에서는 환불 신청을 받을 수 없어요. 고객센터로 문의해주세요.'
                  : '결제 이후 서비스를 이용하셔서 환불이 어려워요. 구독 해지만 가능해요.'}
              </p>
            )}
          </>
        ) : (
          <EmptyState
            icon={<IconInfoCircle />}
            title="환불 신청 가능한 결제 건이 없어요"
            description="이미 환불된 결제 건뿐이에요. 결제 내역에서 확인해주세요."
          />
        )}
      </Card>

      <ReasonSelectModal
        open={modalOpen}
        submitting={refundMutation.isPending}
        onClose={handleModalClose}
        onConfirm={(reasonCode, reasonText) => refundMutation.mutate({ reasonCode, reasonText })}
        title="환불 사유를 알려주세요"
        selectLabel="환불 사유"
        etcCode={REFUND_REASON_ETC_CODE}
        confirmLabel="환불 신청하기"
        confirmVariant="primary"
        queryKey={['billing', 'refundReasons']}
        fetchReasons={async (signal) => (await billingApi.getRefundReasons(signal)).data.data}
        reasonTextMaxLength={REFUND_REASON_TEXT_MAX_LENGTH}
        footerNote="결제 후 7일 이내 건은 사용 여부와 관계없이 전액 환불돼요. 신청 후 영업일 기준 3~5일 내로 결제하신 수단으로 환불돼요."
      />
    </Page>
  );
}
